<template>
  <date-picker
    ref="picker"
    class="range-datepicker"
    v-model="dateRange"
    :disabled="disabledDateRange"
    :shortcuts="shortcuts"
    :clearable="clearable"
    :disabled-date="verifyDisabledDate"
    :append-to-body="false"
    :lang="lang"
    :confirm-text="btnText"
    @change="fetchDate"
    @open="openCalender"
    @close="closeCalendar"
    @pick="pickCalendar"
    @panel-change="onChangeCalendar"
    @calendar-change="onChangeCalendar"
    confirm
    range
  >
    <template v-slot:input>
      <v-text-field
        :value="dateRangeValue"
        dense
        outlined
        prepend-inner-icon="mdi-calendar-blank-outline"
        readonly
        hide-details
        class="picker-text"
        :disabled="disabledDateRange"
        :class="{
          'picker-closed': !isPickerOpen,
          'v-input--is-focused': isPickerOpen,
          'picker-open-focus': isPickerOpen,
          'picker-hover': isFieldHovered,
        }"
        @mouseenter.native="onFieldMouseEnter"
        @mouseleave.native="onFieldMouseLeave"
      >
        <template v-slot:append>
          <v-icon class="append-icon" size="16">panda-chev-down</v-icon>
        </template>
      </v-text-field>
    </template>
    <template v-slot:icon-calendar>
      <v-icon style="display: none">mdi-chevron-down</v-icon>
    </template>
  </date-picker>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import {
  format,
  subDays,
  differenceInCalendarDays,
  isSameDay,
  isSameMonth,
  isBefore,
} from 'date-fns'
import { enUS, ptBR, es as esES } from 'date-fns/locale'
import { mapState } from 'vuex'
import ptBr from 'vue2-datepicker/locale/pt-br'
import es from 'vue2-datepicker/locale/es'
import en from 'vue2-datepicker/locale/en'

const zwsp = '\u200B'

ptBr.monthFormat = 'MMMM'
ptBr.formatLocale.weekdaysMin = [
  'D',
  'S',
  'T',
  'Q',
  'Q' + zwsp,
  'S' + zwsp,
  'S' + zwsp + zwsp,
]
es.monthFormat = 'MMMM'
es.formatLocale.weekdaysMin = ['D', 'L', 'M', 'M' + zwsp, 'J', 'V', 'S']
es.formatLocale.months = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
]
es.formatLocale.monthsShort = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
]
es.formatLocale.weekdays = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
]
es.formatLocale.weekdaysShort = [
  'Dom',
  'Lun',
  'Mar',
  'Mié',
  'Jue',
  'Vie',
  'Sáb',
]
es.formatLocale.weekdaysMin = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá']
en.monthFormat = 'MMMM'
en.formatLocale.weekdaysMin = ['S', 'M', 'T', 'W', 'T' + zwsp, 'F', 'S' + zwsp]

export default {
  name: 'RangeDatePicker',
  components: {
    DatePicker,
  },
  props: {
    initialRangeDate: {
      type: Array,
      required: false,
      default: () => [subDays(new Date(), 29), new Date()],
    },
    btnText: {
      type: String,
      required: false,
    },
    shortcuts: {
      type: Array,
      required: false,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: false,
    },
    canSelectToday: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  methods: {
    openCalender() {
      this.tempStartRange = null

      this.isPickerOpen = true

      this.onChangeCalendar()
    },
    closeCalendar() {
      this.isPickerOpen = false
    },
    pickCalendar(event) {
      this.onChangeCalendar()

      if (event instanceof MouseEvent || !event) {
        return
      }

      if (!this.tempStartRange) {
        this.tempStartRange = [new Date(event), new Date(event)]

        return
      }

      if (this.tempStartRange) {
        this.tempStartRange = null

        return
      }
    },
    onChangeCalendar() {
      this.addTooltipToDisabledsCells()
      this.addCellRoundedCorner()
    },
    clampRange(range) {
      const currentRange = this.tempStartRange ?? range

      this.tempStartRange = null

      if (!Array.isArray(currentRange) || currentRange.length === 0) return []

      if (currentRange.length === 1) {
        const d = new Date(currentRange[0])
        return [d, d]
      }

      let [start, end] = currentRange.map((r) => new Date(r))

      const todayLimit = new Date()
      todayLimit.setHours(23, 59, 59, 999)
      if (end > todayLimit) end = todayLimit

      return [start, end]
    },
    fetchDate() {
      this.$nextTick(() => {
        this.dateRange = this.clampRange(this.dateRange)

        this.addTooltipToDisabledsCells()
        this.addCellRoundedCorner()
        this.$emit('fetch-range-date', this.dateRange)
      })
    },
    verifyDisabledDate(date) {
      const newDate = new Date()
      const today = newDate.setDate(newDate.getDate())
      const yesterday = subDays(new Date(), 1)
      const endDate = this.canSelectToday ? today : yesterday
      const canUse = !isBefore(date, endDate)
      return canUse
    },
    addTooltipToDisabledsCells() {
      this.$nextTick(() => {
        const root = this.$refs.picker?.$el
        if (!root) return

        const tooltipText = this.$i18next.t(
          'message.pages.analyticsV3.datapicker.dataUnavailable'
        )

        const disabledsCells = root.querySelectorAll('td.cell.disabled')

        if (disabledsCells.length) {
          disabledsCells.forEach((td) => {
            td.setAttribute('title', tooltipText)
          })
        }
      })
    },
    addCellRoundedCorner() {
      this.$nextTick(() => {
        const root = this.$refs.picker?.$el
        if (!root) return

        root
          .querySelectorAll(
            '.rounded-corners, .rounded-corner-left, .rounded-corner-right'
          )
          .forEach((el) =>
            el.classList.remove(
              'rounded-corners',
              'rounded-corner-left',
              'rounded-corner-right'
            )
          )

        const activeCells = root.querySelectorAll('td.cell.active')

        if (activeCells.length === 2) {
          activeCells[0].classList.add('rounded-corner-left')
          activeCells[1].classList.add('rounded-corner-right')

          return
        }

        activeCells[0].classList.add('rounded-corners')
      })
    },
    onFieldMouseEnter() {
      this.isFieldHovered = true
    },
    onFieldMouseLeave() {
      this.isFieldHovered = false
    },
  },
  data() {
    return {
      tempStartRange: null,
      isPickerOpen: false,
      dateRange: this.initialRangeDate,
      isFieldHovered: false,
    }
  },
  computed: {
    ...mapState('user', {
      userPlanInfo: 'userPlanInfo',
    }),
    ...mapState({
      dateRangePicker: (state) => state.analytics.filters.dateRange,
      disabledDateRange: (state) => state.analytics.disabledDateRange,
    }),
    dateRangeValue() {
      if (!this.dateRange || this.dateRange.length === 0) return ''

      const [startRaw, endRaw] = this.dateRange
      const start = new Date(startRaw)
      const end = endRaw ? new Date(endRaw) : start

      const locale = { pt: ptBR, es: esES, en: enUS }[this.locale]
      const t = this.$i18next.t.bind(this.$i18next)

      const today = new Date()
      const yesterday = subDays(today, 1)

      if (isSameDay(start, yesterday) && isSameDay(end, yesterday)) {
        return t('datepicker.yesterday')
      }

      if (isSameDay(start, today) && isSameDay(end, today)) {
        return t('datepicker.today')
      }
      if (!endRaw || isSameDay(start, end)) {
        return t('datepicker.onlyDate', {
          day: format(start, 'dd', { locale }),
          month: format(end, 'MMMM', { locale }).replace(/^./, (m) =>
            m.toUpperCase()
          ),
          year: format(end, 'yyyy', { locale }),
        })
      }

      const sub = this.userPlanInfo?.subscription
      if (sub) {
        const cStart = new Date(sub.current_period_start * 1000)
        const cEnd = new Date(sub.current_period_end * 1000)

        if (isSameDay(start, cStart) && isSameDay(end, cEnd)) {
          return t('datepicker.currentCycle')
        }

        const len = differenceInCalendarDays(cEnd, cStart)
        const pStart = subDays(cStart, len + 1)
        const pEnd = subDays(cEnd, len + 1)
        if (isSameDay(start, pStart) && isSameDay(end, pEnd)) {
          return t('datepicker.previousCycle')
        }
      }

      const diff = differenceInCalendarDays(end, start)

      if (diff === 7 && isSameDay(end, today)) {
        return t('datepicker.last7Days', {
          start: format(start, 'dd', { locale }),
          // eslint-disable-next-line quotes
          end: format(end, 'dd', { locale }),
          month: format(end, 'MMMM', { locale }).replace(/^./, (m) =>
            m.toUpperCase()
          ),
        })
      }

      if (diff === 30 && isSameDay(end, today)) {
        return t('datepicker.last30Days', {
          start: format(start, 'dd', { locale }),
          // eslint-disable-next-line quotes
          end: format(end, 'dd', { locale }),
          month: format(end, 'MMMM', { locale }).replace(/^./, (m) =>
            m.toUpperCase()
          ),
        })
      }

      if (isSameMonth(start, end)) {
        return t('datepicker.sameMonth', {
          start: format(start, 'dd', { locale }),
          // eslint-disable-next-line quotes
          end: format(end, 'dd', { locale }),
          month: format(end, 'MMMM', { locale }).replace(/^./, (m) =>
            m.toUpperCase()
          ),
        })
      }

      return t('datepicker.crossMonth', {
        // eslint-disable-next-line quotes
        start: format(start, 'dd', { locale }),
        // eslint-disable-next-line quotes
        first_month: format(start, 'MMMM', { locale }).replace(/^./, (m) =>
          m.toUpperCase()
        ),
        // eslint-disable-next-line quotes
        end: format(end, 'dd', { locale }),
        // eslint-disable-next-line quotes
        second_month: format(end, 'MMMM', { locale }).replace(/^./, (m) =>
          m.toUpperCase()
        ),
      })
    },
    locale() {
      return this.$i18next.resolvedLanguage
    },
    lang() {
      switch (this.locale) {
        case 'pt':
        case 'pt-BR':
          return ptBr
        case 'es':
        case 'es-ES':
          return es
        default:
          return en
      }
    },
  },
  watch: {
    dateRangePicker(newVal) {
      this.dateRange = [newVal.start, newVal.end]
    },
  },
  beforeDestroy() {
    this.tempStartRange = null
  },
}
</script>

<style lang="scss">
@import '~vue2-datepicker/scss/index.scss';

.range-datepicker {
  flex: 1 0 0;
  align-items: center;
  margin: 0 !important;

  .append-icon {
    transform: translateY(4px);
  }

  .picker-text,
  .picker-text * {
    user-select: none !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    cursor: pointer !important;
  }

  .picker-text ::selection {
    background: transparent !important;
  }
}

.mx-datepicker-range {
  .mx-btn-double-left,
  .mx-btn-double-right,
  .mx-icon-double-left,
  .mx-icon-double-right {
    display: none !important;
  }
}

.mx-datepicker-footer {
  border: none !important;
}

.mx-calendar {
  border: none !important;
}

.mx-datepicker-sidebar {
  display: flex !important;
  flex-direction: column !important;
  width: auto !important;
  padding: 0 16px !important;
  gap: 16px !important;
}

.mx-datepicker-main {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  border-radius: 8px !important;
  background-color: varFigma(neutral-100-white) !important;
  border-color: varFigma(neutral-95-lightest) !important;
}

.mx-datepicker-sidebar,
.mx-datepicker-content {
  margin: 16px 0 !important;
  padding: 0 16px !important;
  border-color: varFigma(neutral-95-lightest) !important;
}

.cell {
  color: varFigma(neutral-20-dark) !important;
}

.cell.active {
  color: #ffffff !important;
  background-color: varFigma(brand-60-base) !important;

  &:hover {
    color: varFigma(neutral-30-base) !important;
  }
}

.cell.in-range {
  color: #ffffff !important;
  background-color: varFigma(brand-60-base) !important;

  &:hover {
    background-color: varFigma(brand-95-lighter) !important;
    color: varFigma(neutral-20-dark) !important;
    border-radius: 0px !important;
  }
}

.cell.hover-in-range {
  background-color: varFigma(brand-95-lighter) !important;
}

.cell.not-current-month {
  opacity: 0;
  cursor: default;
}

.cell {
  font-size: 12px !important;
  font-weight: 400 !important;

  &:hover {
    background-color: varFigma(brand-95-lighter) !important;
    border-radius: 8px !important;
  }
}

.cell.disabled {
  color: varFigma(neutral-80-lighter) !important;
  background-color: transparent !important;
}

.mx-btn-text {
  color: varFigma(neutral-30-base) !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  padding: 0 2px !important;

  &:hover {
    color: varFigma(brand-60-base) !important;
  }
}

.rounded-corners {
  border-radius: 8px !important;
}

.rounded-corner-left {
  border-top-left-radius: 8px !important;
  border-bottom-left-radius: 8px !important;
}

.rounded-corner-right {
  border-top-right-radius: 8px !important;
  border-bottom-right-radius: 8px !important;
}

.mx-datepicker-btn-confirm {
  padding: 8px 16px !important;
  border-radius: 8px !important;
  color: var(--neutral-100-white-light) !important;
  background-color: var(--brand-60-base-light) !important;
}

.mx-icon-left:before {
  font-size: 14px !important;
  width: 14px !important;
  height: 14px !important;
  color: varFigma(neutral-30-base);
}

.mx-icon-left:hover:before {
  color: varFigma(brand-60-base) !important;
}

.mx-icon-right:before {
  font-size: 14px !important;
  width: 14px !important;
  height: 14px !important;
  color: varFigma(neutral-30-base);
}

.mx-icon-right:hover:before {
  color: varFigma(brand-60-base) !important;
}

.picker-closed .v-input__icon,
.picker-closed .v-icon {
  width: 16px !important;
  min-width: 16px !important;
  font-size: 16px;
  color: #7283f3 !important;
}

.picker-closed .append-icon.v-icon {
  color: varFigma(neutral-60) !important;
}

.picker-closed.v-text-field.v-text-field--outlined fieldset {
  border-color: varFigma(neutral-95-lightest) !important;
}

.picker-closed .v-text-field.v-text-field--outlined:hover fieldset {
  color: varFigma(brand-60-base) !important;
  border-color: varFigma(brand-60-base) !important;
}

.picker-open-focus {
  color: varFigma(brand-60-base) !important;
  border-color: varFigma(brand-60-base) !important;
}

.picker-open-focus .v-input__icon,
.picker-open-focus .v-icon {
  width: 16px !important;
  min-width: 16px !important;
  font-size: 16px;
  color: #7283f3 !important;
}

.picker-open-focus .append-icon.v-icon {
  color: varFigma(brand-60-base) !important;
}

.picker-text {
  font-family: 'Rubik';
  font-size: 14px;
  font-weight: 400;
  user-select: none;
}

.picker-text .v-input__slot input {
  color: varFigma(neutral-20-dark) !important;
}

.picker-text.v-input--is-disabled .v-input__slot input {
  color: varFigma(neutral-70) !important;
}

.picker-closed.v-input--is-disabled .v-input__icon,
.picker-closed.v-input--is-disabled .v-icon {
  color: varFigma(neutral-70) !important;
}

.picker-text.v-input--is-disabled .v-input__control {
  background-color: varFigma(neutral-95-lightest) !important;
  border: 1px solid;
  border-color: varFigma(neutral-80-lighter) !important;
}

.picker-text .v-input__slot {
  padding: 0 8px !important;
  .v-input__prepend-inner {
    padding-right: 8px !important;
  }
}

.picker-text
  .v-input__prepend-inner
  .v-input__icon--prepend-inner
  .v-icon.v-icon--disabled {
  color: varFigma(neutral-70) !important;
}

.picker-hover:not(.v-input--is-disabled) .v-input__control:hover {
  box-shadow: 0 0 0 1px varFigma(brand-60-base) !important;
}
</style>
